import { CustomClaimValue, Icons, OrganizationAdminSummary, Organizations, useAuth } from "@app/shared";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toastError, toastSuccess } from "../../../helpers";
import { useConfirmDialog, useFetch } from "../../../hooks";
import { AuthorizeView, DropDownMenu, IconLink, PageContainer, SearchInput, SortableTable, SortableTableHeaderType } from "../../shared";

export const OrganizationAdmin: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { setCurrentOrganizationId } = useAuth();
    const [summaries, , helpers] = useFetch(() => Organizations.getAdminSummaries());
    const confirm = useConfirmDialog();

    const handleDelete = (organization: OrganizationAdminSummary) => {
        confirm.show(
            "Delete Organization?",
            `Delete "${organization.name}"?`,
            "Delete",
            async () => {
                try {
                    await Organizations.deleteOrganization(organization.id);
                    toastSuccess("Organization deleted", `"${organization.name}" has been deleted.`);
                    await helpers.refreshData();
                } catch (error: any) {
                    toastError(`Unable to delete organization: ${error}`);
                }
            }
        );
    };

    const filteredData = useMemo(() => {
        const search = searchParams.get("q")?.trim().toLowerCase();
        if (!search?.length)
            return summaries;

        return summaries?.filter(o => o.name?.toLowerCase().includes(search));
    }, [summaries, searchParams]);

    const columns: SortableTableHeaderType<OrganizationAdminSummary>[] = [
        {
            header: "Organization",
            dataKey: "name",
            width: 250
        },
        {
            header: "Devices",
            dataKey: "deviceCount",
            width: 120
        },
        {
            header: "Users",
            dataKey: "userCount",
            width: 120
        },
        {
            header: "Locations",
            dataKey: "locationCount",
            width: 120
        },
        {
            header: "Delete Eligible",
            dataKey: "canBeDeleted",
            width: 140,
            renderFunc: d => d?.canBeDeleted ? "Yes" : "No"
        },
        {
            header: "",
            width: 20,
            renderFunc: o => (
                <DropDownMenu>
                    <IconLink
                        Icon={Icons.Trash}
                        onClick={(evt) => {
                            evt.stopPropagation();
                            o?.canBeDeleted && handleDelete(o);
                        }}
                        className={o?.canBeDeleted ? "" : "pointer-events-none opacity-50"}
                    >
                        Delete
                    </IconLink>
                </DropDownMenu>
            )
        }
    ];

    return (
        <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
            <PageContainer title="Organizations">
                <div className="mb-4 flex justify-end">
                    <SearchInput
                        initialSearch={searchParams.get("q")}
                        onSearch={(terms) => {
                            setSearchParams(existing => {
                                if (terms) {
                                    existing.set("q", terms);
                                } else {
                                    existing.delete("q");
                                }
                                return existing;
                            });
                        }}
                    />
                </div>
                <SortableTable
                    isLoading={helpers.isLoading}
                    initialSortKey="name"
                    onRowClick={async (rowData) => {
                        if (!rowData.id) return;
                        await setCurrentOrganizationId(rowData.id);
                    }}
                    columns={columns}
                    data={filteredData}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[10, 25, 50]}
                    emptyValue={<div className="my-5">No organizations found</div>}
                />
            </PageContainer>
            {confirm.renderDialog()}
        </AuthorizeView>
    );
};
